import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MONTH_LABELS,
  USER_ROLE_LABELS,
  WEEKDAY_LABELS,
  eventHasEnded,
  formatDateSpan,
  isOperationalWeekend,
  monthGrid,
  nextAvailabilityMark,
  roleCanSetAvailability,
  roleCanViewOthersAvailability,
  shiftYearMonth,
  yearMonthFromDate,
  type AvailabilityListResponse,
  type AvailabilityMark,
  type AvailabilityPeopleResponse,
  type AvailabilityPerson,
  type ApiTokenCreateResponse,
  type ApiTokenListResponse,
  type ApiTokenResponse,
  type CalendarFeedCreateResponse,
  type CalendarFeedListResponse,
  type CalendarFeedResponse,
  type PublicApiToken,
  type PublicCalendarFeedToken,
  type TournamentListRow,
  type TournamentMonthResponse,
  type TournamentWeekend,
  type WeekendRosterResponse,
} from '@inventohub/shared';
import { HelpLink } from '../components/HelpLink';
import { ApiClientError, apiDownload, apiRequest, triggerDownload } from '../lib/api';
import { useAuth } from '../lib/auth';

function chicagoToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function markFor(
  date: string,
  days: ReadonlyMap<string, 'available' | 'unavailable'>,
): AvailabilityMark {
  return days.get(date) ?? 'unknown';
}

function TournamentMonthCard({
  monthLabel,
  weekends,
  isOwn,
  today,
  busyEventId,
  onRequest,
  onWithdraw,
}: {
  monthLabel: string;
  weekends: TournamentWeekend[] | null;
  isOwn: boolean;
  today: string;
  busyEventId: string | null;
  onRequest: (eventId: string) => void;
  onWithdraw: (eventId: string) => void;
}): JSX.Element {
  return (
    <div className="card" data-testid="availability-tournaments">
      <h2 data-testid="availability-tournaments-title">Tournaments in {monthLabel}</h2>
      {weekends === null ? <p className="meta">Loading tournaments…</p> : null}
      {weekends?.map((weekend) => (
        <section key={weekend.friday} data-testid={`availability-weekend-${weekend.friday}`}>
          <h3>{formatDateSpan(weekend.friday, weekend.sunday)}</h3>
          {weekend.events.length === 0 ? (
            <p data-testid={`availability-weekend-empty-${weekend.friday}`}>No tournaments</p>
          ) : (
            <ul className="link-list">
              {weekend.events.map((row) => (
                <TournamentRow
                  key={`${weekend.friday}-${row.eventId}`}
                  row={row}
                  friday={weekend.friday}
                  isOwn={isOwn}
                  ended={eventHasEnded(row.endDate, today)}
                  busy={busyEventId === row.eventId}
                  onRequest={onRequest}
                  onWithdraw={onWithdraw}
                />
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function TournamentRow({
  row,
  friday,
  isOwn,
  ended,
  busy,
  onRequest,
  onWithdraw,
}: {
  row: TournamentListRow;
  friday: string;
  isOwn: boolean;
  ended: boolean;
  busy: boolean;
  onRequest: (eventId: string) => void;
  onWithdraw: (eventId: string) => void;
}): JSX.Element {
  const canAct = isOwn && !ended && !busy;
  return (
    <li data-testid={`availability-tournament-${row.eventId}-${friday}`}>
      <Link to={`/events/${row.eventId}`}>{row.name}</Link>
      <p className="meta" data-testid={`availability-tournament-detail-${row.eventId}-${friday}`}>
        {row.detail}
      </p>
      <div className="button-row">
        {row.state === 'assigned' ? (
          isOwn ? (
            <Link
              to={`/events/${row.eventId}`}
              data-testid={`availability-assigned-${row.eventId}-${friday}`}
            >
              Assigned
            </Link>
          ) : (
            <span data-testid={`availability-assigned-${row.eventId}-${friday}`}>Assigned</span>
          )
        ) : null}
        {row.state === 'requested' ? (
          <span data-testid={`availability-state-${row.eventId}-${friday}`}>Requested</span>
        ) : null}
        {row.state === 'declined' ? (
          <span data-testid={`availability-state-${row.eventId}-${friday}`}>Declined</span>
        ) : null}
        {row.state === 'requested' && canAct ? (
          <button
            type="button"
            className="button button--ghost button--compact"
            onClick={() => onWithdraw(row.eventId)}
            data-testid={`availability-withdraw-${row.eventId}-${friday}`}
          >
            Withdraw
          </button>
        ) : null}
        {row.state === 'declined' && canAct ? (
          <button
            type="button"
            className="button button--ghost button--compact"
            onClick={() => onRequest(row.eventId)}
            data-testid={`availability-request-again-${row.eventId}-${friday}`}
          >
            Request again
          </button>
        ) : null}
        {row.state === 'none' && canAct ? (
          <button
            type="button"
            className="button button--ghost button--compact"
            onClick={() => onRequest(row.eventId)}
            data-testid={`availability-request-${row.eventId}-${friday}`}
          >
            Request
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function AvailabilityPage(): JSX.Element {
  const { userId: routeUserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUse = user ? roleCanSetAvailability(user.role) : false;
  const canViewOthers = user ? roleCanViewOthersAvailability(user.role) : false;
  const viewingId = routeUserId && canViewOthers ? routeUserId : (user?.id ?? '');
  const isOwn = Boolean(user && viewingId === user.id);

  const today = chicagoToday();
  const initial = yearMonthFromDate(today);
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);
  const [days, setDays] = useState<Map<string, 'available' | 'unavailable'>>(new Map());
  const [people, setPeople] = useState<AvailabilityPerson[]>([]);
  const [roster, setRoster] = useState<WeekendRosterResponse | null>(null);
  const [bulkFrom, setBulkFrom] = useState('');
  const [bulkTo, setBulkTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyDate, setBusyDate] = useState<string | null>(null);
  const [feed, setFeed] = useState<PublicCalendarFeedToken | null>(null);
  const [feedOnce, setFeedOnce] = useState<{ raw: string; url: string } | null>(null);
  const [tokens, setTokens] = useState<PublicApiToken[]>([]);
  const [tokenOnce, setTokenOnce] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<TournamentMonthResponse | null>(null);
  const [tournamentKey, setTournamentKey] = useState<string | null>(null);
  const [requestBusy, setRequestBusy] = useState<string | null>(null);
  const visibleTournamentKey = `${year}-${month}-${viewingId}`;

  const cells = useMemo(() => monthGrid(year, month), [year, month]);
  const rangeFrom = cells[0]?.date ?? today;
  const rangeTo = cells[cells.length - 1]?.date ?? today;

  useEffect(() => {
    if (!canUse || !viewingId) return;
    let cancelled = false;
    void (async () => {
      try {
        const query = new URLSearchParams({ from: rangeFrom, to: rangeTo });
        if (canViewOthers && viewingId !== user?.id) query.set('userId', viewingId);
        const body = await apiRequest<AvailabilityListResponse>(
          `/api/availability?${query.toString()}`,
        );
        if (cancelled) return;
        setDays(new Map(body.days.map((day) => [day.date, day.status])));
        setError(null);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load availability.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUse, canViewOthers, viewingId, rangeFrom, rangeTo, user?.id]);

  useEffect(() => {
    if (!canUse) return;
    let cancelled = false;
    void (async () => {
      try {
        const [peopleBody, feedBody, tokenBody] = await Promise.all([
          apiRequest<AvailabilityPeopleResponse>('/api/availability/people'),
          isOwn
            ? apiRequest<CalendarFeedListResponse>('/api/me/calendar-feed')
            : Promise.resolve({ token: null }),
          isOwn && user
            ? apiRequest<ApiTokenListResponse>(`/api/users/${user.id}/tokens`)
            : Promise.resolve({ tokens: [] }),
        ]);
        if (cancelled) return;
        setPeople(peopleBody.users);
        setFeed(feedBody.token);
        setTokens(tokenBody.tokens);
      } catch {
        if (!cancelled) {
          /* roster and tokens are optional chrome */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUse, isOwn, user]);

  useEffect(() => {
    if (!canViewOthers) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<WeekendRosterResponse>('/api/availability/weekend');
        if (!cancelled) setRoster(body);
      } catch {
        if (!cancelled) setRoster(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canViewOthers]);

  useEffect(() => {
    if (!canUse || !viewingId) return;
    let cancelled = false;
    const key = `${year}-${month}-${viewingId}`;
    void (async () => {
      try {
        const query = new URLSearchParams({ year: String(year), month: String(month) });
        if (canViewOthers && viewingId !== user?.id) query.set('userId', viewingId);
        const body = await apiRequest<TournamentMonthResponse>(
          `/api/availability/tournaments?${query.toString()}`,
        );
        if (!cancelled) {
          setTournaments(body);
          setTournamentKey(key);
        }
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load tournaments.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUse, canViewOthers, viewingId, year, month, user?.id]);

  if (!user || !canUse) {
    return (
      <section className="card" data-testid="availability-page">
        <h2>Availability</h2>
        <p className="status status--error">
          Viewer and event-code sessions cannot use the availability calendar.
        </p>
      </section>
    );
  }

  async function reloadTournaments(): Promise<void> {
    const query = new URLSearchParams({ year: String(year), month: String(month) });
    if (canViewOthers && user && viewingId !== user.id) query.set('userId', viewingId);
    const body = await apiRequest<TournamentMonthResponse>(
      `/api/availability/tournaments?${query.toString()}`,
    );
    setTournaments(body);
    setTournamentKey(visibleTournamentKey);
  }

  async function onRequestTournament(eventId: string): Promise<void> {
    if (!isOwn) return;
    setRequestBusy(eventId);
    setError(null);
    try {
      await apiRequest(`/api/events/${eventId}/assignment-requests`, {
        method: 'POST',
        body: {},
      });
      await reloadTournaments();
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not request that tournament.',
      );
    } finally {
      setRequestBusy(null);
    }
  }

  async function onWithdrawTournament(eventId: string): Promise<void> {
    if (!isOwn) return;
    setRequestBusy(eventId);
    setError(null);
    try {
      await apiRequest(`/api/events/${eventId}/assignment-requests`, {
        method: 'DELETE',
        body: {},
      });
      await reloadTournaments();
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not withdraw that request.',
      );
    } finally {
      setRequestBusy(null);
    }
  }

  async function onTapDay(date: string): Promise<void> {
    if (!isOwn) return;
    const current = markFor(date, days);
    const next = nextAvailabilityMark(current);
    setBusyDate(date);
    setError(null);
    try {
      await apiRequest(`/api/availability/${date}`, {
        method: 'PUT',
        body: { status: next },
      });
      setDays((currentDays) => {
        const copy = new Map(currentDays);
        if (next === 'unknown') copy.delete(date);
        else copy.set(date, next);
        return copy;
      });
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save that day.');
    } finally {
      setBusyDate(null);
    }
  }

  async function onBulk(mode: 'available_except' | 'only_available'): Promise<void> {
    if (!isOwn || !bulkFrom || !bulkTo) return;
    setError(null);
    setNotice(null);
    try {
      await apiRequest('/api/availability/bulk', {
        method: 'POST',
        body: { from: bulkFrom, to: bulkTo, mode },
      });
      const body = await apiRequest<AvailabilityListResponse>(
        `/api/availability?from=${rangeFrom}&to=${rangeTo}`,
      );
      setDays(new Map(body.days.map((day) => [day.date, day.status])));
      setNotice(
        mode === 'available_except'
          ? 'Range marked available. Tap days to mark exceptions.'
          : 'Range marked unavailable. Tap days you can work.',
      );
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not apply the range.');
    }
  }

  async function onDownloadIcs(): Promise<void> {
    setError(null);
    try {
      const { blob, filename } = await apiDownload('/api/me/events.ics');
      triggerDownload(blob, filename || 'inventohub-events.ics');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not download the calendar.',
      );
    }
  }

  async function onMintFeed(): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<CalendarFeedCreateResponse>('/api/me/calendar-feed', {
        method: 'POST',
        body: {},
      });
      setFeed(body.token);
      setFeedOnce({ raw: body.raw, url: body.url });
      setNotice('Copy the subscribe URL now. It will not be shown again.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not mint a feed URL.');
    }
  }

  async function onRevokeFeed(): Promise<void> {
    setError(null);
    try {
      await apiRequest<CalendarFeedResponse>('/api/me/calendar-feed/revoke', {
        method: 'POST',
        body: {},
      });
      setFeed(null);
      setFeedOnce(null);
      setNotice('Calendar subscribe URL revoked.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not revoke the feed.');
    }
  }

  async function onMintToken(): Promise<void> {
    if (!user) return;
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<ApiTokenCreateResponse>(`/api/users/${user.id}/tokens`, {
        method: 'POST',
        body: {},
      });
      const listed = await apiRequest<ApiTokenListResponse>(`/api/users/${user.id}/tokens`);
      setTokens(listed.tokens);
      setTokenOnce(body.raw);
      setNotice('Copy this machine token now. It will not be shown again.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not mint a token.');
    }
  }

  async function onRevokeToken(token: PublicApiToken): Promise<void> {
    if (!user) return;
    setError(null);
    try {
      await apiRequest<ApiTokenResponse>(`/api/users/${user.id}/tokens/${token.id}/revoke`, {
        method: 'POST',
        body: {},
      });
      const listed = await apiRequest<ApiTokenListResponse>(`/api/users/${user.id}/tokens`);
      setTokens(listed.tokens);
      setTokenOnce(null);
      setNotice(`Revoked the token starting ${token.tokenPrefix}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not revoke that token.');
    }
  }

  function onSelectPerson(event: FormEvent<HTMLSelectElement>): void {
    const nextId = event.currentTarget.value;
    if (!user) return;
    if (nextId === user.id) navigate('/availability');
    else navigate(`/availability/${nextId}`);
  }

  const monthLabel = `${MONTH_LABELS[month - 1] ?? ''} ${year}`;
  const viewingName = people.find((person) => person.id === viewingId)?.name ?? user.name;

  return (
    <section data-testid="availability-page">
      <div className="card">
        <div className="card__title-row">
          <h2>Availability</h2>
          <HelpLink slug="availability" />
        </div>
        <p className="meta">
          Mark days you can work. Unmarked days stay unknown. Tapping a day only changes that mark.
        </p>
        {error ? (
          <p className="status status--error" data-testid="availability-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="status status--ok" data-testid="availability-notice">
            {notice}
          </p>
        ) : null}
        {canViewOthers ? (
          <label className="field">
            Person
            <select value={viewingId} onChange={onSelectPerson} data-testid="availability-person">
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                  {person.id === user.id ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <p className="meta" data-testid="availability-subject">
          {isOwn ? 'Your calendar' : `${viewingName} — view only`}
        </p>
      </div>

      <div className="card">
        <div className="calendar-nav">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              const next = shiftYearMonth(year, month, -1);
              setYear(next.year);
              setMonth(next.month);
            }}
            data-testid="availability-prev"
          >
            Previous
          </button>
          <h3 data-testid="availability-month">{monthLabel}</h3>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              const next = shiftYearMonth(year, month, 1);
              setYear(next.year);
              setMonth(next.month);
            }}
            data-testid="availability-next"
          >
            Next
          </button>
        </div>
        <div className="calendar-weekdays" aria-hidden="true">
          {WEEKDAY_LABELS.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
        <div className="calendar-grid" data-testid="availability-grid">
          {cells.map((cell) => {
            const mark = markFor(cell.date, days);
            const weekend = isOperationalWeekend(cell.date);
            return (
              <button
                key={cell.date}
                type="button"
                className={[
                  'calendar-day',
                  `calendar-day--${mark}`,
                  weekend ? 'calendar-day--weekend' : '',
                  cell.inMonth ? '' : 'calendar-day--outside',
                  cell.date === today ? 'calendar-day--today' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={!isOwn || busyDate === cell.date}
                onClick={() => void onTapDay(cell.date)}
                data-testid={`availability-day-${cell.date}`}
                aria-label={`${cell.date} ${mark}`}
              >
                <span className="calendar-day__num">{Number(cell.date.slice(8))}</span>
                <span className="calendar-day__mark">{mark === 'unknown' ? '' : mark}</span>
              </button>
            );
          })}
        </div>
      </div>

      <TournamentMonthCard
        monthLabel={monthLabel}
        weekends={tournamentKey === visibleTournamentKey ? (tournaments?.weekends ?? null) : null}
        isOwn={isOwn}
        today={today}
        busyEventId={requestBusy}
        onRequest={(eventId) => void onRequestTournament(eventId)}
        onWithdraw={(eventId) => void onWithdrawTournament(eventId)}
      />

      {isOwn ? (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault();
          }}
          data-testid="availability-bulk"
        >
          <h2>Date range</h2>
          <label className="field">
            From
            <input
              type="date"
              value={bulkFrom}
              onChange={(event) => setBulkFrom(event.target.value)}
              data-testid="availability-bulk-from"
            />
          </label>
          <label className="field">
            To
            <input
              type="date"
              value={bulkTo}
              onChange={(event) => setBulkTo(event.target.value)}
              data-testid="availability-bulk-to"
            />
          </label>
          <div className="button-row">
            <button
              type="button"
              className="button"
              disabled={!bulkFrom || !bulkTo}
              onClick={() => void onBulk('available_except')}
              data-testid="availability-bulk-available"
            >
              Available any except
            </button>
            <button
              type="button"
              className="button button--ghost"
              disabled={!bulkFrom || !bulkTo}
              onClick={() => void onBulk('only_available')}
              data-testid="availability-bulk-only"
            >
              Only available on
            </button>
          </div>
          <p className="meta">Then tap days in that range to flip exceptions.</p>
        </form>
      ) : null}

      {canViewOthers && roster ? (
        <div className="card" data-testid="availability-roster">
          <h2>This weekend</h2>
          <p className="meta">
            {roster.window.start} – {roster.window.end} (Fri–Sun, America/Chicago). Viewers omitted.
          </p>
          <ul className="link-list">
            {roster.people.map((person) => (
              <li key={person.userId} data-testid={`availability-roster-${person.userId}`}>
                <Link to={`/availability/${person.userId}`}>{person.name}</Link>
                <p className="meta">
                  {USER_ROLE_LABELS[person.role]} ·{' '}
                  {person.days.map((day) => `${day.date.slice(5)} ${day.mark}`).join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {isOwn ? (
        <div className="card" data-testid="availability-ics">
          <h2>Assigned events calendar</h2>
          <p className="meta">
            Download or subscribe to events you are assigned to. Availability marks are not in the
            file.
          </p>
          <button
            type="button"
            className="button"
            onClick={() => void onDownloadIcs()}
            data-testid="availability-ics-download"
          >
            Download .ics
          </button>
          {feedOnce ? (
            <p className="status status--ok" data-testid="availability-feed-url">
              Copy this subscribe URL now: {feedOnce.url}
            </p>
          ) : null}
          <button
            type="button"
            className="button button--ghost"
            onClick={() => void onMintFeed()}
            data-testid="availability-feed-mint"
          >
            {feed ? 'Rotate subscribe URL' : 'Create subscribe URL'}
          </button>
          {feed && !feed.revokedAt ? (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => void onRevokeFeed()}
              data-testid="availability-feed-revoke"
            >
              Revoke subscribe URL
            </button>
          ) : null}
        </div>
      ) : null}

      {isOwn ? (
        <div className="card" data-testid="availability-mcp">
          <h2>Grok Bot token</h2>
          <p className="meta">
            Staff and equipment admin tokens can set your availability. Event tools stay Admin-only.
            The raw secret is shown once.
          </p>
          {tokenOnce ? (
            <p className="status status--ok" data-testid="availability-token-raw">
              Copy this token now. It will not be shown again: {tokenOnce}
            </p>
          ) : null}
          <button
            type="button"
            className="button"
            onClick={() => void onMintToken()}
            data-testid="availability-token-mint"
          >
            {tokens.some((row) => !row.revokedAt) ? 'Rotate token' : 'Mint token'}
          </button>
          <ul className="link-list">
            {tokens.map((token) => (
              <li key={token.id}>
                <p>
                  {token.tokenPrefix}…{token.revokedAt ? ' · revoked' : ''}
                </p>
                {!token.revokedAt ? (
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => void onRevokeToken(token)}
                  >
                    Revoke
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
